/**
 * Bottom-nav unread/new-content badge poller (iter-91, refined iter-92).
 *
 * Pings GET /api/users/me/nav-badges every 30s and exposes `{feed, friends,
 * groups}` booleans. Components that "consume" the section (e.g. opening the
 * Feed page) call `markSeen(section)` to clear the dot immediately and POST
 * the seen timestamp to the backend so it survives across refreshes.
 *
 * iter-92 additions:
 *   - The section the user is CURRENTLY viewing (based on react-router
 *     location) never shows a dot — even if new content lands during the
 *     30s poll window — so the indicator can never compete with the page
 *     the user is already looking at.
 *   - Subtle haptic buzz (`navigator.vibrate(20)`) on every fresh
 *     false→true transition so the user feels a tiny "ping" even when
 *     they're on another tile.
 */
import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '@/lib/api';

const POLL_INTERVAL_MS = 30000;

const NavBadgesContext = createContext({
  badges: { feed: false, friends: false, groups: false, dm_unread: 0 },
  markSeen: () => {},
  refetch: () => {},
});

// Map a pathname to its corresponding badge section, or null if none.
function sectionFor(pathname) {
  if (!pathname) return null;
  if (pathname === '/feed' || pathname.startsWith('/feed/')) return 'feed';
  if (pathname === '/friends' || pathname.startsWith('/dm')) return 'friends';
  if (pathname === '/groups' || pathname.startsWith('/groups/')) return 'groups';
  return null;
}

function vibrate(ms = 20) {
  try {
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(ms);
    }
  } catch { /* ignore */ }
}

export function NavBadgesProvider({ children }) {
  const [badges, setBadges] = useState({ feed: false, friends: false, groups: false, dm_unread: 0 });
  // Tracks the last `markSeen` per section so an in-flight poll request can't
  // resurrect a dot the user just dismissed.
  const seenAtRef = useRef({});
  // Holds the previous-badge values so we can fire a haptic only on
  // false→true transitions (not on every poll while the badge is true).
  const prevRef = useRef({ feed: false, friends: false, groups: false });
  const isFirstFetchRef = useRef(true);
  const location = useLocation();
  const currentSection = sectionFor(location.pathname);
  const currentSectionRef = useRef(currentSection);
  useEffect(() => { currentSectionRef.current = currentSection; }, [currentSection]);

  const fetchBadges = useCallback(async () => {
    // Skip polling entirely on BecAIm Business routes — the business app has
    // its own auth and its own nav; the consumer nav-badges endpoint would
    // 401 and (pre iter-101) trigger a redirect to the consumer /login.
    try {
      const path = typeof window !== 'undefined' ? (window.location.pathname || '') : '';
      if (path === '/' || path.startsWith('/business') || path.startsWith('/procurement') || path.startsWith('/sales')) return;
    } catch { /* ignore */ }
    try {
      const { data } = await api.get('/users/me/nav-badges');
      const cutoff = Date.now() - 5000;
      const cur = currentSectionRef.current;
      const overlay = (sec, val) => {
        // Section the user is CURRENTLY viewing is always treated as seen.
        if (cur === sec) return false;
        // Recent local markSeen wins over a stale poll response.
        if (seenAtRef.current[sec] && seenAtRef.current[sec] > cutoff) return false;
        return val;
      };
      const next = {
        feed: overlay('feed', !!data.feed),
        friends: overlay('friends', !!data.friends),
        groups: overlay('groups', !!data.groups),
        dm_unread: data.dm_unread || 0,
      };
      // Detect false→true transitions and fire a single haptic per fetch
      // (one buzz even if multiple sections lit up). Skip the very first
      // fetch on mount so a returning user doesn't get buzzed for badges
      // that were already there before they opened the app.
      if (!isFirstFetchRef.current) {
        const newlyTrue = ['feed', 'friends', 'groups'].some(
          (s) => !prevRef.current[s] && next[s]
        );
        if (newlyTrue) vibrate(20);
      }
      prevRef.current = { feed: next.feed, friends: next.friends, groups: next.groups };
      isFirstFetchRef.current = false;
      setBadges(next);
    } catch {
      // Silent — bottom-nav badges are decoration; never break navigation.
    }
  }, []);

  const markSeen = useCallback(async (section) => {
    if (!['feed', 'friends', 'groups'].includes(section)) return;
    seenAtRef.current[section] = Date.now();
    setBadges((prev) => ({ ...prev, [section]: false }));
    prevRef.current = { ...prevRef.current, [section]: false };
    try {
      await api.post('/users/me/nav-badges/seen', { section });
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchBadges();
    const id = setInterval(fetchBadges, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchBadges]);

  // Whenever the user lands on a relevant page, immediately mark that
  // section seen — the page-level mount also calls markSeen for backend
  // persistence; this layer ensures the visual dot disappears even if the
  // page itself hasn't mounted yet (e.g. during route transition).
  useEffect(() => {
    if (currentSection) {
      seenAtRef.current[currentSection] = Date.now();
      setBadges((prev) => ({ ...prev, [currentSection]: false }));
      prevRef.current = { ...prevRef.current, [currentSection]: false };
    }
  }, [currentSection]);

  return (
    <NavBadgesContext.Provider value={{ badges, markSeen, refetch: fetchBadges }}>
      {children}
    </NavBadgesContext.Provider>
  );
}

export function useNavBadges() {
  return useContext(NavBadgesContext);
}
